import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Link as LinkIcon, Save, X, Eye, EyeOff, QrCode, GripVertical } from "lucide-react";
import { apiClient, API, formatApiError } from "../../lib/api";
import { getToken } from "../../lib/api";

const ICONS = [
    ["briefcase", "Aktentasche"],
    ["hardhat", "Bauhelm"],
    ["zap", "Blitz"],
    ["sparkles", "Funken"],
    ["wrench", "Schraubenschlüssel"],
    ["truck", "LKW"],
    ["leaf", "Blatt"],
];

const emptyJob = {
    title: "",
    position: "",
    suffix: "(m/w/d)",
    description: "",
    tasks: [],
    profile: [],
    icon: "briefcase",
    active: true,
    sort_order: 0,
    salary_min: "",
    salary_max: "",
    salary_unit: "YEAR",
    expiration_date: "",
};

const SALARY_UNITS = [
    ["YEAR", "pro Jahr"],
    ["MONTH", "pro Monat"],
    ["HOUR", "pro Stunde"],
];

const num = (v) => {
    const n = Number(v);
    return v !== "" && v != null && !isNaN(n) && n > 0 ? n : null;
};

export const JobManagement = () => {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [dragIdx, setDragIdx] = useState(null);

    const onDragStart = (idx) => setDragIdx(idx);

    const onDragOver = (e, idx) => {
        e.preventDefault();
        if (dragIdx === null || dragIdx === idx) return;
        setJobs((prev) => {
            const list = [...prev];
            const [moved] = list.splice(dragIdx, 1);
            list.splice(idx, 0, moved);
            return list;
        });
        setDragIdx(idx);
    };

    const onDragEnd = async () => {
        if (dragIdx === null) return;
        setDragIdx(null);
        try {
            await apiClient.post("/admin/jobs/reorder", { ids: jobs.map((j) => j.id) });
            toast.success("Reihenfolge gespeichert.");
        } catch (err) {
            toast.error("Reihenfolge konnte nicht gespeichert werden.");
            fetchJobs();
        }
    };

    const fetchJobs = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await apiClient.get("/admin/jobs");
            setJobs(data.items || []);
        } catch (err) {
            if (err?.response?.status !== 401) {
                toast.error(formatApiError(err?.response?.data?.detail) || "Stellen konnten nicht geladen werden.");
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchJobs();
    }, [fetchJobs]);

    const openEdit = (job) => {
        setEditing({
            ...job,
            tasksText: (job.tasks || []).join("\n"),
            profileText: (job.profile || []).join("\n"),
        });
    };

    const openNew = () => {
        setEditing({ ...emptyJob, tasksText: "", profileText: "", sort_order: jobs.length + 1 });
    };

    const saveJob = async () => {
        if (!editing.title.trim() || !editing.position.trim()) {
            toast.error("Titel und Bereichsname sind Pflichtfelder.");
            return;
        }
        setSaving(true);
        const payload = {
            title: editing.title.trim(),
            position: editing.position.trim(),
            suffix: editing.suffix || "",
            description: editing.description || "",
            tasks: editing.tasksText.split("\n").map((s) => s.trim()).filter(Boolean),
            profile: editing.profileText.split("\n").map((s) => s.trim()).filter(Boolean),
            icon: editing.icon || "briefcase",
            active: !!editing.active,
            sort_order: Number(editing.sort_order) || 0,
            salary_min: num(editing.salary_min),
            salary_max: num(editing.salary_max),
            salary_unit: editing.salary_unit || "YEAR",
            expiration_date: editing.expiration_date || null,
        };
        try {
            if (editing.id) {
                await apiClient.put(`/admin/jobs/${editing.id}`, payload);
                toast.success("Stelle gespeichert.");
            } else {
                await apiClient.post("/admin/jobs", payload);
                toast.success("Stelle angelegt.");
            }
            setEditing(null);
            fetchJobs();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Speichern fehlgeschlagen.");
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (job) => {
        try {
            await apiClient.put(`/admin/jobs/${job.id}`, {
                title: job.title,
                position: job.position,
                suffix: job.suffix || "",
                description: job.description || "",
                tasks: job.tasks || [],
                profile: job.profile || [],
                icon: job.icon || "briefcase",
                active: !job.active,
                sort_order: job.sort_order || 0,
                salary_min: job.salary_min ?? null,
                salary_max: job.salary_max ?? null,
                salary_unit: job.salary_unit || "YEAR",
                expiration_date: job.expiration_date || null,
            });
            toast.success(!job.active ? "Stelle ist jetzt online." : "Stelle wurde offline genommen.");
            fetchJobs();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Änderung fehlgeschlagen.");
        }
    };

    const deleteJob = async (job) => {
        if (!window.confirm(`Stelle "${job.title}" wirklich löschen? Bestehende Bewerbungen bleiben erhalten.`)) return;
        try {
            await apiClient.delete(`/admin/jobs/${job.id}`);
            toast.success("Stelle gelöscht.");
            fetchJobs();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Löschen fehlgeschlagen.");
        }
    };

    const downloadQr = async (job) => {
        try {
            const res = await fetch(
                `${API}/admin/jobs/${job.id}/qrcode?base=${encodeURIComponent(window.location.origin)}`,
                { headers: { Authorization: `Bearer ${getToken()}` } }
            );
            if (!res.ok) throw new Error();
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `qr_${job.slug}.png`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("QR-Code heruntergeladen – ideal für Aushänge und Flyer.");
        } catch {
            toast.error("QR-Code konnte nicht erstellt werden.");
        }
    };

    const copyShareLink = async (job) => {
        const url = `${window.location.origin}/api/share/${job.slug}`;
        try {
            await navigator.clipboard.writeText(url);
            toast.success("Teilen-Link kopiert – ideal für Facebook, WhatsApp & Co.");
        } catch {
            toast.error("Link konnte nicht kopiert werden.");
        }
    };

    const inputCls =
        "w-full px-3 py-2.5 border border-moebus-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-moebus-green";

    return (
        <div data-testid="job-management" className="space-y-6">
            <div className="flex items-center justify-between">
                <p className="text-sm text-slate-500">
                    Zeilen am <GripVertical size={13} className="inline -mt-0.5" />-Griff ziehen, um die Reihenfolge auf der
                    Website zu ändern. Offline geschaltete Stellen verschwinden von der Website, bleiben aber hier erhalten.
                </p>
                <button
                    data-testid="new-job-btn"
                    onClick={openNew}
                    className="inline-flex items-center gap-2 bg-moebus-green hover:bg-emerald-700 text-white font-medium px-4 py-2.5 rounded-md text-sm"
                >
                    <Plus size={15} /> Neue Stelle
                </button>
            </div>

            <div className="bg-white border border-moebus-border rounded-lg overflow-hidden">
                <table data-testid="jobs-table" className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-moebus-border">
                        <tr className="text-left">
                            <th className="w-10 px-3 py-3"></th>
                            <th className="px-4 py-3 font-medium text-slate-600">Stelle</th>
                            <th className="px-4 py-3 font-medium text-slate-600">Bereich</th>
                            <th className="px-4 py-3 font-medium text-slate-600">Sichtbarkeit</th>
                            <th className="px-4 py-3 font-medium text-slate-600 text-right">Aktion</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan={5} className="p-8 text-center text-slate-500">Lade…</td>
                            </tr>
                        )}
                        {!loading && jobs.map((job, idx) => (
                            <tr
                                key={job.id}
                                data-testid={`job-row-${job.id}`}
                                draggable
                                onDragStart={() => onDragStart(idx)}
                                onDragOver={(e) => onDragOver(e, idx)}
                                onDragEnd={onDragEnd}
                                className={`border-b border-moebus-border last:border-0 hover:bg-slate-50 ${
                                    dragIdx === idx ? "bg-emerald-50 opacity-80" : ""
                                }`}
                            >
                                <td className="px-3 py-3 text-slate-300 cursor-grab active:cursor-grabbing" title="Ziehen zum Sortieren">
                                    <GripVertical size={16} data-testid={`drag-handle-${job.id}`} />
                                </td>
                                <td className="px-4 py-3 font-medium text-moebus-deep">
                                    {job.title} <span className="text-xs text-slate-400 font-normal">{job.suffix}</span>
                                </td>
                                <td className="px-4 py-3 text-slate-600">{job.position}</td>
                                <td className="px-4 py-3">
                                    <button
                                        data-testid={`toggle-job-${job.id}`}
                                        onClick={() => toggleActive(job)}
                                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                                            job.active
                                                ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                                                : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                                        }`}
                                    >
                                        {job.active ? <Eye size={13} /> : <EyeOff size={13} />}
                                        {job.active ? "Online" : "Offline"}
                                    </button>
                                </td>
                                <td className="px-4 py-3 text-right">
                                    <div className="inline-flex items-center gap-3">
                                        <button
                                            data-testid={`qr-job-${job.id}`}
                                            onClick={() => downloadQr(job)}
                                            title="QR-Code herunterladen"
                                            className="text-slate-500 hover:text-moebus-green"
                                        >
                                            <QrCode size={16} />
                                        </button>
                                        <button
                                            data-testid={`share-job-${job.id}`}
                                            onClick={() => copyShareLink(job)}
                                            title="Teilen-Link kopieren"
                                            className="text-slate-500 hover:text-moebus-green"
                                        >
                                            <LinkIcon size={16} />
                                        </button>
                                        <button
                                            data-testid={`edit-job-${job.id}`}
                                            onClick={() => openEdit(job)}
                                            className="inline-flex items-center gap-1 text-moebus-green hover:text-emerald-700 text-sm font-medium"
                                        >
                                            <Pencil size={14} /> Bearbeiten
                                        </button>
                                        <button
                                            data-testid={`delete-job-${job.id}`}
                                            onClick={() => deleteJob(job)}
                                            className="text-red-600 hover:text-red-800"
                                            title="Löschen"
                                        >
                                            <Trash2 size={15} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Edit dialog */}
            {editing && (
                <div
                    data-testid="job-edit-dialog"
                    className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4"
                    onClick={(e) => e.target === e.currentTarget && setEditing(null)}
                >
                    <div className="bg-white w-full max-w-2xl max-h-[95vh] md:max-h-[90vh] overflow-y-auto rounded-t-2xl md:rounded-lg shadow-2xl">
                        <div className="sticky top-0 bg-white border-b border-moebus-border px-6 py-4 flex items-center justify-between">
                            <h2 className="font-heading font-bold text-xl text-moebus-deep">
                                {editing.id ? "Stelle bearbeiten" : "Neue Stelle"}
                            </h2>
                            <button
                                data-testid="close-job-dialog"
                                onClick={() => setEditing(null)}
                                className="text-slate-400 hover:text-slate-700"
                            >
                                <X size={22} />
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-semibold uppercase tracking-widest text-moebus-deep mb-1.5">
                                        Stellentitel *
                                    </label>
                                    <input
                                        data-testid="job-title-input"
                                        type="text"
                                        value={editing.title}
                                        onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                                        className={inputCls}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold uppercase tracking-widest text-moebus-deep mb-1.5">
                                        Bereichsname (im Formular) *
                                    </label>
                                    <input
                                        data-testid="job-position-input"
                                        type="text"
                                        value={editing.position}
                                        onChange={(e) => setEditing({ ...editing, position: e.target.value })}
                                        className={inputCls}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold uppercase tracking-widest text-moebus-deep mb-1.5">
                                        Zusatz
                                    </label>
                                    <input
                                        data-testid="job-suffix-input"
                                        type="text"
                                        value={editing.suffix}
                                        onChange={(e) => setEditing({ ...editing, suffix: e.target.value })}
                                        className={inputCls}
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs font-semibold uppercase tracking-widest text-moebus-deep mb-1.5">
                                            Symbol
                                        </label>
                                        <select
                                            data-testid="job-icon-select"
                                            value={editing.icon}
                                            onChange={(e) => setEditing({ ...editing, icon: e.target.value })}
                                            className={inputCls}
                                        >
                                            {ICONS.map(([val, label]) => (
                                                <option key={val} value={val}>{label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold uppercase tracking-widest text-moebus-deep mb-1.5">
                                            Reihenfolge
                                        </label>
                                        <input
                                            data-testid="job-order-input"
                                            type="number"
                                            value={editing.sort_order}
                                            onChange={(e) => setEditing({ ...editing, sort_order: e.target.value })}
                                            className={inputCls}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold uppercase tracking-widest text-moebus-deep mb-1.5">
                                    Beschreibung
                                </label>
                                <textarea
                                    data-testid="job-description-input"
                                    value={editing.description}
                                    onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                                    rows={4}
                                    className={`${inputCls} resize-y leading-relaxed`}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold uppercase tracking-widest text-moebus-deep mb-1.5">
                                    Aufgaben (eine pro Zeile)
                                </label>
                                <textarea
                                    data-testid="job-tasks-input"
                                    value={editing.tasksText}
                                    onChange={(e) => setEditing({ ...editing, tasksText: e.target.value })}
                                    rows={5}
                                    className={`${inputCls} resize-y leading-relaxed`}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold uppercase tracking-widest text-moebus-deep mb-1.5">
                                    Profil / Anforderungen (eine pro Zeile)
                                </label>
                                <textarea
                                    data-testid="job-profile-input"
                                    value={editing.profileText}
                                    onChange={(e) => setEditing({ ...editing, profileText: e.target.value })}
                                    rows={4}
                                    className={`${inputCls} resize-y leading-relaxed`}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold uppercase tracking-widest text-moebus-deep mb-1.5">
                                    Gehalt für Google-Stellenanzeige (optional, brutto in €)
                                </label>
                                <div className="grid grid-cols-3 gap-3">
                                    <input
                                        data-testid="job-salary-min-input"
                                        type="number"
                                        min="0"
                                        placeholder="von (z. B. 36000)"
                                        value={editing.salary_min ?? ""}
                                        onChange={(e) => setEditing({ ...editing, salary_min: e.target.value })}
                                        className={inputCls}
                                    />
                                    <input
                                        data-testid="job-salary-max-input"
                                        type="number"
                                        min="0"
                                        placeholder="bis (z. B. 48000)"
                                        value={editing.salary_max ?? ""}
                                        onChange={(e) => setEditing({ ...editing, salary_max: e.target.value })}
                                        className={inputCls}
                                    />
                                    <select
                                        data-testid="job-salary-unit-select"
                                        value={editing.salary_unit || "YEAR"}
                                        onChange={(e) => setEditing({ ...editing, salary_unit: e.target.value })}
                                        className={inputCls}
                                    >
                                        {SALARY_UNITS.map(([val, label]) => (
                                            <option key={val} value={val}>{label}</option>
                                        ))}
                                    </select>
                                </div>
                                <p className="text-xs text-slate-400 mt-1.5">
                                    Wird nicht auf der Website angezeigt, nur als strukturierte Daten für Google Jobs.
                                    Spanne (von–bis) oder nur ein Wert möglich.
                                </p>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold uppercase tracking-widest text-moebus-deep mb-1.5">
                                    Bewerbungsschluss (optional)
                                </label>
                                <div className="flex items-center gap-3">
                                    <input
                                        data-testid="job-expiration-input"
                                        type="date"
                                        value={editing.expiration_date || ""}
                                        onChange={(e) => setEditing({ ...editing, expiration_date: e.target.value })}
                                        className={`${inputCls} max-w-[220px]`}
                                    />
                                    {editing.expiration_date && (
                                        <button
                                            data-testid="job-expiration-clear"
                                            onClick={() => setEditing({ ...editing, expiration_date: "" })}
                                            className="text-xs text-slate-500 hover:text-red-600 underline"
                                        >
                                            Entfernen
                                        </button>
                                    )}
                                </div>
                                <p className="text-xs text-slate-400 mt-1.5">
                                    Erscheint auf der Stellen-Seite und im Indeed-Feed als Ablaufdatum. Leer lassen, wenn die Stelle unbefristet ausgeschrieben ist.
                                </p>
                            </div>
                            {editing.slug && (
                                <div>
                                    <label className="block text-xs font-semibold uppercase tracking-widest text-moebus-deep mb-1.5">
                                        WhatsApp / Social-Media Vorschau
                                    </label>
                                    <img
                                        data-testid="share-preview-image"
                                        src={`${API}/share-image/${editing.slug}?v=${encodeURIComponent(editing.title || "")}`}
                                        alt={`Vorschaubild für ${editing.title}`}
                                        className="w-full max-w-md rounded-lg border border-moebus-border shadow-sm"
                                        loading="lazy"
                                    />
                                    <p className="text-xs text-slate-400 mt-1.5">
                                        So erscheint der geteilte Link in WhatsApp, Facebook & Co. Das Bild wird automatisch
                                        aus dem Stellentitel erzeugt – Titeländerungen sind nach dem Speichern sichtbar.
                                    </p>
                                </div>
                            )}
                            <label className="flex items-center gap-2.5 cursor-pointer select-none w-fit">
                                <input
                                    data-testid="job-active-checkbox"
                                    type="checkbox"
                                    checked={!!editing.active}
                                    onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                                    className="w-4 h-4 accent-emerald-600"
                                />
                                <span className="text-sm text-slate-700">Stelle ist online sichtbar</span>
                            </label>
                            <div className="flex gap-3 pt-2">
                                <button
                                    data-testid="save-job-btn"
                                    onClick={saveJob}
                                    disabled={saving}
                                    className="flex-1 inline-flex items-center justify-center gap-2 bg-moebus-green hover:bg-emerald-700 text-white font-medium px-5 py-2.5 rounded-md text-sm disabled:opacity-60"
                                >
                                    <Save size={14} /> {saving ? "Speichern…" : "Speichern"}
                                </button>
                                <button
                                    onClick={() => setEditing(null)}
                                    className="inline-flex items-center justify-center gap-2 bg-white border border-moebus-border text-slate-700 px-5 py-2.5 rounded-md text-sm hover:bg-slate-50"
                                >
                                    Abbrechen
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
